import { Container, Service } from 'typedi';

import { AnnouncementService } from '../services/AnnouncementService';
import { isFailedPrecondition } from '../services/apiError';
import { VersionCardStore } from '../stores/VersionCardStore';
import type { IAnnouncementEntity } from '../types/announcement';
import { toLocalInput } from '../utils/announcement';
import { sameVersion } from '../utils/validation';
import { SessionController } from './SessionController';

type TCardAction = () => Promise<IAnnouncementEntity | null>;

@Service()
export class VersionCardController {
  private readonly _store = Container.get(VersionCardStore);
  private readonly _service = Container.get(AnnouncementService);
  private readonly _session = Container.get(SessionController);

  saveDraft = (): Promise<void> => this.run(this.saveAnd());

  publish = (): Promise<void> => {
    this._store.setConfirming(null);
    return this.run(this.saveAnd(saved => this._service.publish(saved.id)));
  };

  schedule = (): Promise<void> => this.run(this.saveAnd(this.scheduleSaved));

  /** «Сохранить» of a live card: a published one asks first, a scheduled one stores and moves its time if changed. */
  saveLive = (): Promise<void> => {
    if (this._store.published) {
      this._store.setConfirming('edit');
      return Promise.resolve();
    }
    return this.run(this.saveAnd(this.reschedule));
  };

  // A published card has no audience, CTA or end_at: only the texts, Important and the pin travel.
  confirmEdit = (): Promise<void> => {
    this._store.setConfirming(null);
    const { card } = this._store;
    if (!card) return Promise.resolve();
    return this.run(async () => {
      const { important, pinned, localizations, notifyAgain } = this._store;
      const saved = await this._service.editPublished({ id: card.id, important, pinned, localizations, notifyAgain });
      this._store.setNotifyAgain(false);
      return saved;
    });
  };

  confirmWithdraw = (): Promise<void> => {
    this._store.setConfirming(null);
    const { card } = this._store;
    if (!card) return Promise.resolve();
    return this.run(() => this._service.withdraw(card.id));
  };

  /** Opens the card of the typed version if the service already has one; true when it did. */
  private openExisting = async (): Promise<boolean> => {
    const items = await this._service.list();
    const existing = items.find(
      a => a.businessVersion !== undefined && sameVersion(a.businessVersion, this._store.version),
    );
    if (existing) this._store.setRedirect({ to: `/announcements/${existing.id}`, replace: false });
    return existing !== undefined;
  };

  /** Stores the card; on the new-card screen opens an existing card of that version instead and returns null. */
  private save = async (): Promise<IAnnouncementEntity | null> => {
    const { card } = this._store;
    if (!card && (await this.openExisting())) return null;
    try {
      return await this._service.upsertVersionCard({
        businessVersion: card?.businessVersion ?? this._store.version.trim(),
        localizations: this._store.localizations,
        pinned: this._store.pinned,
        important: this._store.important,
      });
    } catch (e) {
      // Created or published by someone else since the List check: open that card.
      if (!card && isFailedPrecondition(e) && (await this.openExisting())) return null;
      throw e;
    }
  };

  /** Stores the texts first (UpsertVersionCard), then runs `then` on the stored card. */
  private saveAnd =
    (then?: (saved: IAnnouncementEntity) => Promise<IAnnouncementEntity>): TCardAction =>
    async () => {
      const saved = await this.save();
      return saved && then ? then(saved) : saved;
    };

  // datetime-local is the manager's local time; Date turns it into the instant the service gets in UTC.
  private scheduleSaved = (saved: IAnnouncementEntity): Promise<IAnnouncementEntity> =>
    this._service.schedule(saved.id, new Date(this._store.publishAt));

  // A scheduled card keeps its time unless the manager changed it; Schedule then moves it.
  private reschedule = async (saved: IAnnouncementEntity): Promise<IAnnouncementEntity> =>
    this._store.publishAt === toLocalInput(saved.publishAt) ? saved : this.scheduleSaved(saved);

  // A new card moves to its own URL, which loads it again; an open card updates in place.
  private show = (card: IAnnouncementEntity): void => {
    if (!this._store.card) {
      this._store.setRedirect({ to: `/announcements/${card.id}`, replace: true });
      return;
    }
    this._store.show(card);
  };

  private run = async (action: TCardAction): Promise<void> => {
    this._store.startAction();
    try {
      const result = await action();
      if (result) this.show(result);
    } catch (e) {
      const error = this._session.reportError(e);
      if (error) this._store.setServerError(error);
    } finally {
      this._store.finishAction();
    }
  };
}
